import React, { useEffect, useState } from "react";
import { api, errorMessage } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Copy, CheckCheck } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = {
  email: "Email",
  social: "Social",
  sms: "SMS",
  phone: "Phone Script",
};
const TYPE_CLASS = {
  email: "bg-emerald-500/10 text-emerald-300 border-emerald-500/20",
  social: "bg-blue-500/10 text-blue-300 border-blue-500/20",
  sms: "bg-amber-500/10 text-amber-300 border-amber-500/20",
  phone: "bg-violet-500/10 text-violet-300 border-violet-500/20",
};

export default function SponsorMarketingKit() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/sponsor/marketing-templates");
        setItems(data);
      } catch (e) {
        toast.error(errorMessage(e));
      }
    })();
  }, []);

  const grouped = {
    email: items.filter((t) => t.type === "email"),
    social: items.filter((t) => t.type === "social"),
    sms: items.filter((t) => t.type === "sms"),
    phone: items.filter((t) => t.type === "phone"),
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-semibold tracking-tight">Marketing Kit</h1>
        <p className="mt-1 text-sm text-slate-400">Pre-approved templates you can copy and send.</p>
      </div>

      <Tabs defaultValue="email" className="w-full">
        <TabsList className="bg-slate-900 border border-slate-800">
          {["email","social","sms","phone"].map((k) => (
            <TabsTrigger
              key={k}
              value={k}
              className="data-[state=active]:bg-emerald-500/15 data-[state=active]:text-emerald-300 text-slate-300"
              data-testid={`tab-${k}`}
            >
              {TYPE_LABEL[k]} <span className="ml-2 text-[11px] text-slate-500">{grouped[k].length}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {["email","social","sms","phone"].map((k) => (
          <TabsContent key={k} value={k} className="mt-5">
            <div className={`grid gap-4 ${k === "phone" ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"}`}>
              {grouped[k].map((t) => <TemplateCard key={t.template_id} t={t} tall={k === "phone"} />)}
              {grouped[k].length === 0 && (
                <div className="rounded-md border border-dashed border-slate-800 p-8 text-center text-sm text-slate-500">
                  No templates yet.
                </div>
              )}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function TemplateCard({ t, tall }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(t.content);
      setCopied(true);
      toast.success("Copied to clipboard");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Copy failed");
    }
  };
  return (
    <Card className="border-slate-800 bg-slate-900 p-5" data-testid={`tpl-${t.template_id}`}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={TYPE_CLASS[t.type]}>{TYPE_LABEL[t.type]}</Badge>
          <Badge variant="outline" className="bg-emerald-500/10 text-emerald-300 border-emerald-500/20">Approved</Badge>
        </div>
        <Button size="sm" onClick={copy} className="bg-emerald-600 text-white hover:bg-emerald-500" data-testid={`tpl-copy-${t.template_id}`}>
          {copied ? <CheckCheck className="mr-1 h-3 w-3" /> : <Copy className="mr-1 h-3 w-3" />}
          {copied ? "Copied" : "Copy"}
        </Button>
      </div>
      <div className="text-sm font-semibold text-slate-100">{t.title}</div>
      <pre className={`mt-3 whitespace-pre-wrap rounded-md border border-slate-800 bg-slate-950 p-4 font-sans text-xs text-slate-300 ${tall ? "min-h-[220px]" : "min-h-[140px]"}`}>{t.content}</pre>
    </Card>
  );
}
